import * as React from "react";

export type OAuthProvider = "google" | "apple" | "kakao" | "naver" | "line";

export const PROVIDER_ICONS: Record<OAuthProvider, React.ReactNode> = {
  google: (
    <svg className="size-5 absolute left-4" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  ),
  apple: (
    // Apple's path occupies a smaller area within the 24×24 viewBox than the
    // other brand glyphs, so we bump it up one step to look optically equal.
    <svg
      className="size-6 absolute left-4"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  ),
  kakao: (
    <svg
      className="size-5 absolute left-4"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M12 3C6.48 3 2 6.48 2 10.5c0 2.52 1.64 4.74 4.12 6.03-.13.47-.84 3.03-.87 3.24 0 0-.02.15.08.21.1.06.22.01.22.01.29-.04 3.37-2.21 3.9-2.58.85.12 1.72.19 2.55.19 5.52 0 10-3.48 10-7.1S17.52 3 12 3z" />
    </svg>
  ),
  naver: (
    // Naver's path fills the entire 24×24 viewBox (no built-in padding) so the
    // glyph reads larger than the others at the same nominal size. Drop one
    // step and nudge right ~2px so the optical center lines up with the rest
    // of the icons sitting at left-4.
    <svg className="size-4 absolute left-4.5" viewBox="0 0 24 24">
      <path
        fill="#03C75A"
        d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727z"
      />
    </svg>
  ),
  line: (
    <svg className="size-5 absolute left-4" viewBox="0 0 24 24">
      <path
        fill="#00B900"
        d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"
      />
    </svg>
  ),
};

export const PROVIDER_TRANSLATION_KEYS: Record<OAuthProvider, string> = {
  google: "loginWithGoogle",
  apple: "loginWithApple",
  kakao: "loginWithKakao",
  naver: "loginWithNaver",
  line: "loginWithLine",
};

/**
 * Locale-specific OAuth provider ordering.
 * - Korean: Kakao + Naver prioritized, then Google/Apple
 * - Japanese: LINE prioritized, then Google/Apple
 * - Others: Google/Apple only
 */
export function getSocialProviders(locale: string): OAuthProvider[] {
  const base: OAuthProvider[] = ["google", "apple"];
  if (locale === "ko") return ["kakao", "naver", ...base];
  if (locale === "ja") return ["line", ...base];
  return base;
}
